import type { AriaRole, ButtonHTMLAttributes, HTMLAttributes } from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import type { DefinedDefaults, TWithDataComponent } from "@/ui/lib/types";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const SONNER_VARIANT_CLASS_MAP = {
  default: "border-outline-dark bg-surface-default text-foreground",
  success: "border-functional-success bg-functional-success text-foreground-muted-strong [&_[data-component='Icon']]:text-functional-success [&_[data-component='SonnerTitle']]:text-functional-success",
  destructive: "border-functional-destructive bg-functional-destructive text-foreground-muted-strong [&_[data-component='Icon']]:text-functional-destructive [&_[data-component='SonnerTitle']]:text-functional-destructive",
} as const;

export const SONNER_ROOT_CLASS = "w-[21rem] max-w-full rounded-sys-xs border border-solid p-4 shadow-md outline-none";

/** Focus glow from the Figma "Focus" state: a 2px ring plus a deeper elevation shadow. */
export const SONNER_ROOT_FOCUS_CLASS = "focus-visible:shadow-[0px_0px_0px_2px_rgba(0,0,0,0.2),0px_4px_12px_0px_rgba(0,0,0,0.1)]";

export const SONNER_CONTENT_CLASS = "flex min-w-0 flex-1 flex-col gap-2";

export const SONNER_ICON_CLASS = "size-6 shrink-0 text-foreground";

export const SONNER_ICON_LOADING_CLASS = "animate-spin text-foreground-muted";

export const SONNER_DESCRIPTION_CLASS = "text-foreground-muted-strong";

export const SONNER_ACTION_CLASS = "shrink-0 self-start";

export const SONNER_CLOSE_CLASS = "inline-flex size-6 shrink-0 items-center justify-center rounded-sys-xs text-foreground transition-colors hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:text-foreground-muted";

/** Icon shown in place of the leading icon while the toast is loading. */
export const SONNER_LOADING_ICON = "loader" satisfies TIconKey;

export const SONNER_ICON_MAP: Record<TSonnerVariant, TIconKey> = {
  default: "circleTick",
  success: "circleTick",
  destructive: "circleCross",
};

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TSonnerVariant = keyof typeof SONNER_VARIANT_CLASS_MAP;

export type TSonnerAction = {
  label: string;
  onClick: () => void;
};

export type TSonnerProps = {
  variant?: TSonnerVariant;
  title: string;
  description?: string;
  detail?: string;
  icon?: TIconKey | null;
  loading?: boolean;
  action?: TSonnerAction;
  onDismiss?: () => void;
  dismissLabel?: string;
} & TWithDataComponent & Omit<HTMLAttributes<HTMLDivElement>, "title">;

export type TSonnerCloseProps = ButtonHTMLAttributes<HTMLButtonElement>;

type TSonnerDefaultProps = DefinedDefaults<
  TSonnerProps,
  "variant" |
  "loading"
> & { role: AriaRole; tabIndex: number };

export type TUseSonner = {
  dismissLabel: string;
};

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const SONNER_DEFAULTS = {
  variant: "default",
  loading: false,
  role: "status",
  tabIndex: 0,
} satisfies TSonnerDefaultProps;
